import { useMemo, useState } from 'react';
import { expertiseDekking, fmtUren } from '@ep/core';
import { useData } from '../lib/data';
import { Badge, Laden } from '../ui/ui';

const groen = (n: number) => (n >= 100 ? '#15803d' : n >= 75 ? '#22a05a' : n >= 50 ? '#6cc08b' : '#a8dbb8');

/** Expertise-matrix: kennisniveau per engineer (namen verticaal, geen maximum) en dekking per expertise. */
export function ExpertiseMatrix() {
  const { data: d, kal } = useData();
  const [weken, setWeken] = useState(8);
  const [offertes, setOffertes] = useState(false);
  const engs = useMemo(() => (d ? d.engineers.filter((e) => e.actief !== false).sort((a, b) => a.naam.localeCompare(b.naam)) : []), [d]);
  const dekking = useMemo(
    () => (d && kal ? expertiseDekking({ kal, projecten: d.projecten, taken: d.taken, takenpakketten: d.takenpakketten, expertises: d.expertises.map((x) => x.naam), vandaag: d.vandaag, weken, offertesMeetellen: offertes }) : []),
    [d, kal, weken, offertes],
  );
  if (!d) return <Laden />;
  const totVraag = dekking.reduce((s, x) => s + x.vraag, 0);
  const totAanbod = dekking.reduce((s, x) => s + x.aanbod, 0);
  return (
    <div className="pagina">
      <div className="paneel">
        <div className="sectie-kop">
          <div className="groei">
            <h1>Expertise-matrix en dekking</h1>
            <div className="klein mute">Links het kennisniveau per engineer (25–100%). Rechts per expertise: vraag = nog niet ingepland werk van actieve projecten, aanbod = vrije uren × kennisniveau.</div>
          </div>
          <select value={weken} onChange={(e) => setWeken(Number(e.target.value))} style={{ width: 130 }}>
            {[4, 8, 13].map((w) => <option key={w} value={w}>{w} weken</option>)}
          </select>
          <label className="check"><input type="checkbox" checked={offertes} onChange={(e) => setOffertes(e.target.checked)} /> Offertes meetellen</label>
        </div>
        <div className="klein" style={{ color: 'var(--rood)', marginBottom: 8 }}>
          Totaal: vraag {fmtUren(Math.round(totVraag))}u · aanbod {fmtUren(Math.round(totAanbod))}u · dekking {totVraag ? Math.round((totAanbod / totVraag) * 100) : '–'}%
          {dekking.some((x) => x.oordeel === 'tekort') && ` · ⚠ tekort: ${dekking.filter((x) => x.oordeel === 'tekort').map((x) => x.expertise).join(', ')}`}
          {dekking.some((x) => x.kwetsbaar) && ` · kwetsbaar (≤ 1 specialist): ${dekking.filter((x) => x.kwetsbaar).map((x) => x.expertise).join(', ')}`}
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="tabel matrix">
            <thead>
              <tr>
                <th style={{ verticalAlign: 'bottom' }}>Expertise</th>
                {engs.map((e) => (
                  <th key={e.id} className="vert" title={e.naam}>
                    <div>{e.naam}</div>
                  </th>
                ))}
                <th style={{ verticalAlign: 'bottom' }}>≥75% / totaal</th>
                <th className="rechts" style={{ verticalAlign: 'bottom' }}>Vraag</th>
                <th className="rechts" style={{ verticalAlign: 'bottom' }}>Aanbod</th>
                <th style={{ verticalAlign: 'bottom' }}>Dekking</th>
              </tr>
            </thead>
            <tbody>
              {dekking.map((r) => (
                <tr key={r.expertise}>
                  <td className="vet nowrap">{r.expertise}</td>
                  {engs.map((e) => {
                    const n = e.expertises.find((x) => x.naam === r.expertise)?.niveau;
                    return (
                      <td key={e.id} className="cel" title={`${e.naam}: ${n ? n + '%' : 'geen'}`}>
                        {n ? <span style={{ background: groen(n) }}>{n}</span> : null}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: 'center', color: r.kwetsbaar ? 'var(--rood)' : undefined, fontWeight: r.kwetsbaar ? 700 : 400 }}>{r.specialisten} / {r.totaal}</td>
                  <td className="rechts">{fmtUren(Math.round(r.vraag))}u</td>
                  <td className="rechts">{fmtUren(Math.round(r.aanbod))}u</td>
                  <td>
                    {r.dekking == null ? (
                      <Badge toon="grijs">geen vraag</Badge>
                    ) : (
                      <Badge toon={r.oordeel === 'tekort' ? 'rood' : r.oordeel === 'krap' ? 'oranje' : 'groen'}>
                        {Math.round(r.dekking * 100)}% · {r.oordeel === 'tekort' ? `tekort ${r.tekort}u` : r.oordeel}
                      </Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
